import * as React from 'react'
import { ReactElement, useMemo } from 'react'
import {
    Button,
    CardFooter,
    CompoundButton,
    makeStyles,
    mergeClasses,
    shorthands,
    Tooltip,
} from '@fluentui/react-components'
import useViewport, { Viewport } from '../../../hooks/useViewport'
import Card from '../../../components/Card'
import { useFormikContext } from 'formik'
import {
    LegalAidCertificate,
    LegalAidCertificateDto,
    LegalAidCertificateValidationSchema,
} from './LegalAidCertificateForm'
import {
    bundleIcon,
    DocumentPdfFilled,
    DocumentPdfRegular,
    LinkDismissFilled,
    LinkDismissRegular,
} from '@fluentui/react-icons'
import useCurrency from '../../../hooks/useCurrency'
import { LegalAidCertificateProps } from '../../../pages/File/Registration/LegalAidCertificateOverview'
import { stealthClick } from '../../StealthLink'
import useAxios from '../../../hooks/useAxios'
import LinkInvoiceDialog from './LinkInvoiceDialog'
import { CalculationProps } from './Calculation'
import CreateSettlementInvoiceButton from './CreateSettlementInvoiceButton'
import CreateContributionInvoiceButton from './CreateContributionInvoiceButton'

export type LegalAidCertificateInvoiceProps = LegalAidCertificateProps & { calculation: CalculationProps }
const PdfIcon = bundleIcon(DocumentPdfFilled, DocumentPdfRegular)
const UnlinkIcon = bundleIcon(LinkDismissFilled, LinkDismissRegular)

const useStyles = makeStyles({
    footer: {
        display: 'grid',
        gridTemplateColumns: '1fr',
        '& button': {
            justifyContent: 'start',
        },
        ...shorthands.gap(0),
    },
    footerLarge: shorthands.margin('-12px'),
    footerSmall: shorthands.margin('-8px'),
    buttonGroup: {
        display: 'grid',
        gridTemplateColumns: '1fr auto',
    },
})

export default function Invoices(props: LegalAidCertificateInvoiceProps): ReactElement {
    const styles = useStyles()
    const { isViewport } = useViewport()
    const { values, setFieldValue, submitForm } = useFormikContext<LegalAidCertificateDto>()
    const { currency } = useCurrency(values.currency)
    const axios = useAxios()

    const certificate: LegalAidCertificate = useMemo(
        () => props.certificates.data?.find(certificate => certificate.id === props.selected.id),
        [props.selected],
    )

    const canCreateInvoice = useMemo(
        () => values.issued?.length > 0 && LegalAidCertificateValidationSchema.isValidSync(values),
        [values],
    )

    return (
        <Card>
            <CardFooter
                className={mergeClasses(
                    styles.footer,
                    isViewport('<', Viewport.Large) ? styles.footerSmall : styles.footerLarge,
                )}
            >
                {certificate?.contributionInvoice ? (
                    <div className={styles.buttonGroup}>
                        <CompoundButton
                            disabled={!certificate.contributionInvoice.pdfId}
                            secondaryContent={`Open declaratie eigen bijdrage (${certificate.contributionInvoice.number})`}
                            appearance="subtle"
                            icon={<PdfIcon />}
                            onClick={() => stealthClick({ id: certificate.contributionInvoice.pdfId }, axios)}
                        >
                            Eigen bijdrage
                        </CompoundButton>
                        <Tooltip
                            content="Ontkoppel declaratie eigen bijdrage van deze toevoeging. Er wordt geen creditnota aangemaakt."
                            relationship="description"
                            withArrow
                        >
                            <Button
                                icon={<UnlinkIcon />}
                                appearance="subtle"
                                size="large"
                                onClick={() => {
                                    setFieldValue('contributionInvoiceId', null)
                                    submitForm()
                                }}
                            />
                        </Tooltip>
                    </div>
                ) : (
                    <div className={styles.buttonGroup}>
                        <CreateContributionInvoiceButton {...props} canCreateInvoice={canCreateInvoice} />
                        <LinkInvoiceDialog
                            disabled={!canCreateInvoice}
                            fileId={props.file.data.id}
                            currency={currency}
                            certificates={props.certificates.data}
                            targetValue={Number(values.contribution)}
                            tooltipContent="Koppel een bestaande declaratie eigen bijdrage aan deze toevoeging."
                            onSubmit={invoiceId => {
                                setFieldValue('contributionInvoiceId', invoiceId)
                                submitForm()
                            }}
                        />
                    </div>
                )}
                {certificate?.settlementInvoice ? (
                    <div className={styles.buttonGroup}>
                        <CompoundButton
                            disabled={!certificate.settlementInvoice.pdfId}
                            secondaryContent={`Open eindafrekening RvR (${certificate.settlementInvoice.number})`}
                            appearance="subtle"
                            icon={<PdfIcon />}
                            onClick={() => stealthClick({ id: certificate.settlementInvoice.pdfId }, axios)}
                        >
                            Eindafrekening RvR
                        </CompoundButton>
                        <Tooltip
                            content="Ontkoppel de declaratie met de RvR eindafrekening van deze declaratie. Er wordt geen creditnota aangemaakt."
                            relationship="description"
                            withArrow
                        >
                            <Button
                                icon={<UnlinkIcon />}
                                appearance="subtle"
                                size="large"
                                onClick={() => {
                                    setFieldValue('settlementInvoiceId', null)
                                    submitForm()
                                }}
                            />
                        </Tooltip>
                    </div>
                ) : (
                    <div className={styles.buttonGroup}>
                        <CreateSettlementInvoiceButton {...props} canCreateInvoice={canCreateInvoice} />
                        <LinkInvoiceDialog
                            disabled={!canCreateInvoice}
                            fileId={props.file.data.id}
                            certificates={props.certificates.data}
                            currency={currency}
                            targetValue={props.calculation.compensation}
                            tooltipContent="Koppel een bestaande declaratie als RvR eindafrekening aan deze toevoeging."
                            onSubmit={invoiceId => {
                                setFieldValue('settlementInvoiceId', invoiceId)
                                submitForm()
                            }}
                        />
                    </div>
                )}
            </CardFooter>
        </Card>
    )
}
